import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authToken from 'src/utils/auth';
import { useTranslation } from 'react-i18next';
import { CapBoxDefaultProps, CapBoxPropTypes } from 'src/components/CapBox/propTypes';
import { CapFindingContainer } from 'src/components/CapFinding/CapFindingContainer';
import { flatToTree, sectionsWithNumbersFlat } from 'src/utils/form/section';
import { JobExternalStakeholdersPopup } from 'src/components/JobExternalStakeholdersPopup/JobExternalStakeholdersPopup';
import {
  JobExternalStakeholderRoleTranslation,
  JobExternalStakeholdersPopupType,
  ServiceType,
  FindingStatus,
  FindingSectionStatus
} from 'src/components/CapBox/constants';
import { downloadAttachments } from 'src/utils/helpers';
import { Popup } from 'src/components/Popup';
import { STATUSES } from 'src/utils/data/job';
import { SurveyType, CompanyType } from 'src/utils/data/types';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import { useCapHistories } from 'src/hooks/cap';
import { useDeleteJobExternalStakeholder } from 'src/hooks/jobExternalStakeholder/useDeleteJobExternalStakeholder';
import { useExternalJobStakeholders } from 'src/hooks/jobExternalStakeholder/useJobExternalStakeholders';
import Button from 'src/components/Button';
import CapOverallStatus from 'src/components/CapOverallStatus/';
import Loader from 'src/components/Loader';
import logger from 'src/utils/logger';
import s from 'src/components/CapBox/Capbox.styles';
import Styles from 'src/components/JobForm/JobForm.styles';
import Field from 'src/components/Field';
import useGetJobChecklists from 'src/components/JobMobileDataTab/hooks/useGetJobChecklists';
import client from 'src/utils/api/client';
import { EXPORT_CAP } from 'src/graphql/cap/mutation';
import { notification } from 'src/components/Notification';
import { getQuestionNumber } from 'src/components/ChecklistPreviewForm/utils';
import { saveAs } from 'file-saver';
import { compact } from 'lodash';
import { STATE_PROVINCE_QUERY } from 'src/graphql/stateProvince/query';
import parse from 'html-react-parser';

const CapBox = ({
  auth,
  capFindingsData,
  capFindingsLoading,
  jobChecklists,
  companyTypes,
  country,
  job,
  jobStatus,
  updateCapQueryCache,
  jobOverallCapStatus: overallCapStatusValue,
  setJobOverallCapStatus: setOverallCapStatusValue
}) => {
  const { id: jobId } = job;
  const { i18n, t } = useTranslation();
  const { checklists, loading:loadingChecklists, getChecklists } = useGetJobChecklists(jobId);
  const loading = capFindingsLoading || loadingChecklists;
  const [
    getCapHistories,

    { data: capHistoriesData, loading: capHistoriesLoading, refetch: refetchCapHistories },
  ] = useCapHistories({});

  const {
    data: jobExternalStakeholdersData,
    loading: isLoadingJobExternalStakeholders,
    refetch: refetchJobExternalStakeholders
  } = useExternalJobStakeholders(jobId, ServiceType);

  const {
    isLoading: isDeletingJobExternalStakeholder,
    deleteJobExternalStakeholderCallback: deleteJobExternalStakeholder,
  } = useDeleteJobExternalStakeholder();

  const [jobExternalStakeholders, setJobExternalStakeholders] = useState([]);
  const [jobExternalStakeholdersPopupType, setJobExternalStakeholdersPopupType] = useState(null);
  const [filterTag, setFilterTag] = useState('All');
  const [infoCollapsed, setInfoCollapsed] = useState(true);
  const [showCapExportPopup, setShowCapExportPopup] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [factoryAddresses, setFactoryAddresses] = useState('');

  const factoryType = useMemo(() => {
    return (companyTypes.find(v => v.currentVersion.name === CompanyType.Factory) || {
      id: null,
    }).id;
  }, [companyTypes]);

  const companies = useMemo(() => (job.jobCompanies || []).filter(c => !!c.company), [job]);

  const factory = useMemo(() => companies.find(c => {
    if (c.companyTypeId) {
      const companyType = c.company.currentVersion.companyTypes.find(cc => cc.id === factoryType);
      return companyType && companyType.id === c.companyTypeId;
    }

    return c.company.currentVersion.companyTypes.map(cc => cc.id).includes(factoryType);
  }), [companies, factoryType]);

  const getStateProvinceName = async (id) => {
    if (!id) {
      return '';
    }
    try {
      const response = await client.query({
        query: STATE_PROVINCE_QUERY,
        variables: { id },
      });
      return response?.data?.stateProvince?.name || '';
    } catch (err) {
      return '';
    }
  };

  useEffect(() => {
    const getData = async () => {
      const addresses = await Promise.all(
        (factory?.company?.currentVersion?.companyAddresses || [])
          .map(async companyAddress => {
            const { currentVersion } = companyAddress;
            const stateProvinceName = await getStateProvinceName(currentVersion.stateProvinceId);
            return compact([
              (currentVersion.address1 || ''),
              (currentVersion.address2 || ''),
              (currentVersion.city || ''),
              (stateProvinceName || ''),
              (currentVersion.country?.currentVersion?.name || ''),
            ]).join(', ');
          }));
      setFactoryAddresses(compact(addresses).join('.<br/>'));
    };
    getData();
  }, [factory]);

  useEffect(() => {
    setJobExternalStakeholders(jobExternalStakeholdersData ? jobExternalStakeholdersData?.jobExternalStakeholders : []);
  }, [jobExternalStakeholdersData]);

  useEffect(() => {
    if (!capFindingsLoading) {
      getCapHistories({
        variables: { findingIds: capFindingsData?.findings.map(f => f.id) },
      });
    }
  }, [capFindingsLoading, capFindingsData]);

  useEffect(() => {
    if(!isLoadingJobExternalStakeholders) {
      refetchJobExternalStakeholders();
    }
  }, [jobStatus]);

  useEffect(() => {
    if (jobChecklists &&
      Array.isArray(jobChecklists) &&
      jobChecklists.length > 0) {
      getChecklists(jobChecklists.map(({ currentVersion }) => currentVersion.id));
    }
  }, [jobChecklists.map(({ currentVersion }) => currentVersion.id).join(',')]);

  const findings = (capFindingsData && capFindingsData.findings) || [];

  const filteredFindingsByStatus = tag => {
    if (tag === 'All') {
      return findings;
    }

    return findings.filter(f => f.status === tag);
  };
  const filteredFindings =
    filterTag === 'All' ? findings : filteredFindingsByStatus(filterTag);
  const tags = ['All', 'Closed', 'Open'];
  const tagsLabels = {
    All: t('ALL'),
    Closed: t('CLOSED'),
    Open: t('OPEN'),
  };
  
  const isReadyForCap = useMemo(() => jobStatus === STATUSES.ready_for_cap, [jobStatus]);
  const isUnderCap = useMemo(() => jobStatus === STATUSES.under_cap, [jobStatus]);
  const authenticated = authToken();
  const currentUserId = authenticated?.user?.id ? parseInt(authenticated.user.id, 10) : null;
  const allExternalUsersId = jobExternalStakeholders.map(user => user.userId);
  const externalUserResult = allExternalUsersId.length >= 1 ? allExternalUsersId.includes(currentUserId) : false;

  const getDefaultEditableState = (isUnderCapParam, authParam, externalUserResultParam)=>{
    return isUnderCapParam &&
      (authParam.isAdmin || authParam.isCapManager || (
        (authParam.isFactoryUser || authParam.isVendorUser) && externalUserResultParam)
      );
  };

  const isFindingEditable = useCallback((findingStatus = null) => {
    const defaultState = getDefaultEditableState(isUnderCap, auth, externalUserResult);
    return (auth.isVendorUser || auth.isFactoryUser) && findingStatus === FindingSectionStatus.Closed
      ? false
      : defaultState;
  }, [isUnderCap, auth, externalUserResult]);

  const isResponsiblePersonEditable = useCallback((findingStatus = null) => {
    const defaultState = getDefaultEditableState(isUnderCap, auth, externalUserResult);
    return (auth.isVendorUser || auth.isFactoryUser) && findingStatus === FindingSectionStatus.Closed
      ? false
      : defaultState;

  },[isUnderCap, auth, externalUserResult]);

  const isActionEditable = useCallback((findingStatus = null) => {
    /**
     * since status of finding is available in this component
     * a closure is returned with finding status captured
     */
    return (actionStatus = null) => {
      const defaultState = getDefaultEditableState(isUnderCap, auth, externalUserResult);
      return (auth.isVendorUser || auth.isFactoryUser) &&
      (actionStatus === FindingStatus.Approved || findingStatus === FindingSectionStatus.Closed)
        ? false
        : defaultState;
    };
  },[isUnderCap, auth, externalUserResult]);

  const canDownloadAttachment =
    auth.isFactoryUser || auth.isAdmin || auth.isCapManager || auth.isCapManagerRead;
  const forbidEditExternalStakeholders = auth.isClientUser || auth.isFactoryUser || auth.isVendorUser;
  const isExternalStakeholdersEditable = !forbidEditExternalStakeholders && isUnderCap || isReadyForCap;

  const sectionsWithNumbersFlatList = checklists.map(checklist =>
    sectionsWithNumbersFlat(
      flatToTree(
        checklist.currentVersion.checklistSections || [],
        null,
        SurveyType.Checklist)
    )).flat() || [];

  const findingSectionNumber = (findingSectionId) => sectionsWithNumbersFlatList
    .find(sec => sec.id === findingSectionId)?.sectionNumber;

  const questions = sectionsWithNumbersFlatList.map(sec => sec.questions.map(quest => quest )).flat();

  const findingQuestion = (findingChecklistQuestionId) => questions
  .find(quest => quest.checklistQuestionId === findingChecklistQuestionId );

  const findChecklistBySectionId = (findingSectionId) => checklists
  .find(checklist => checklist.currentVersion.checklistSections.find(sec => sec.id === findingSectionId));

  const findingsWithChecklist = filteredFindings.map(filteredfinding => ({checklist: findChecklistBySectionId(filteredfinding.info.sectionId), finding : filteredfinding}));

  const findingsGroupByChecklist = findingsWithChecklist.reduce( (checklistWithFindings, findingWithChecklist) => {
    const itemExists = checklistWithFindings.find(item => findingWithChecklist.checklist === item.checklist)
    if(itemExists){
      itemExists.findings = [...itemExists.findings, findingWithChecklist.finding]
    }else{
      checklistWithFindings.push({checklist: findingWithChecklist.checklist, findings: [findingWithChecklist.finding]})
    }
    return checklistWithFindings;
  }, []);

  if (loading) {
    return <Loader />;
  }

  const jobExternalStakeholdersPopup = (
    <JobExternalStakeholdersPopup
      companyTypes={companyTypes}
      disabled={isLoadingJobExternalStakeholders}
      excludedUsersIds={jobExternalStakeholders.map(user => user.userId)}
      job={job}
      onClose={() => setJobExternalStakeholdersPopupType(null)}
      onSubmit={refetchJobExternalStakeholders}
      serviceType={ServiceType}
      type={jobExternalStakeholdersPopupType}
    />
  );

  const b64toBlob = (base64, type = 'application/octet-stream') => {
    return fetch(`data:${type};base64,${base64}`).then(res => res.blob());
  };

  const getFindingsInfo = () => {
    return findings.map(finding => {
      const checklist = findChecklistBySectionId(finding.info?.sectionId);

      const localFindingSectionNumber = findingSectionNumber(finding.info?.sectionId);
      const localFindingQuestion = findingQuestion(finding.info?.checklistQuestionId);
      const questionNumber = localFindingSectionNumber && localFindingQuestion ? getQuestionNumber(localFindingSectionNumber, localFindingQuestion) : '';

      let findingLabel = '';
      if (finding.info?.section) {
        findingLabel = `${finding.info?.section} > `;
        if (questionNumber) {
          findingLabel += questionNumber;
        }
      }

      return {
        findingId: finding.id,
        path: findingLabel || '',
        checklistName: checklist?.currentVersion?.name || '',
        hasLegalRequirements: checklist?.currentVersion?.hasLegalRequirements || false,
        hasRecommendations: checklist?.currentVersion?.hasRecommendations || false,
      };
    });
  };

  const exportData = async (dataWithAttachments = false) => {
    try {
      setExportingData(true);
      let pdf64 = null;
      try {
        const response = await client.mutate({
          mutation: EXPORT_CAP,
          variables: { jobId: job.id, findingsInfo: getFindingsInfo() },
        });
        pdf64 = response?.data?.exportCap?.pdfBase64;
      } catch (err) {
        logger.error('Export CAP error', err)
      }
      if (!pdf64) {
        notification.error(t('FAILED_TO_EXPORT_CAP'));
        return;
      }
      const pdfBlob = await b64toBlob(pdf64, 'application/pdf');
      if (dataWithAttachments) {
        const attachmentsData = await Promise.all(findings.map(async (finding) => {
          const attachmentsMeta = finding?.actions?.map((action, actionIndex) => {
            if (action?.attachmentsMeta &&
              Array.isArray(action.attachmentsMeta) &&
              action.attachmentsMeta.length > 0) {
              return action.attachmentsMeta.map(attMeta => {
                const data = { ...attMeta };
                data.findingId = finding.id;
                data.actionId = action.id;
                data.actionReference = action.actionReference || actionIndex + 1;
                return data;
              });
            }
            return [];
          });
          return attachmentsMeta.flat();
        }));

        await downloadAttachments(attachmentsData.flat(), {
          zipFolderName: t('EVIDENCES'),
          zipFileName: `${t('CAP_EXPORT')}_${job.reportNumber}`,
          putExtra: true,
          extras: [
            {
              blobData: pdfBlob,
              name: `${t('CAP_EXPORT')}_${job.reportNumber}.pdf`,
            },
          ],
        });
      } else {
        await saveAs(pdfBlob, `${t('CAP_EXPORT')}_${job.reportNumber}.pdf`);
      }
    } catch (err) {
      logger.error('Caught', err);
    } finally {
      setShowCapExportPopup(false);
      setExportingData(false);
    }
  };

  const CAPExportPopup = () => {
    return (<Popup level={0} onClose={() => setShowCapExportPopup(false)}>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {exportingData ?
          <Loader withText={true}/> :
          <>
            <Button orange solid onClick={() => exportData(false)} type="button">{t('EXPORT_CAP_DATA_ONLY')}</Button>
            <Button orange solid onClick={() => exportData(true)} type="button">{t('EXPORT_CAP_DATA_WITH_ATTACHMENTS')}</Button>
          </>}
      </div>
    </Popup>);
  };

  return (
    <>
      {showCapExportPopup && CAPExportPopup()}
      <s.Wrapper id="cap-tab-content">
        {jobExternalStakeholdersPopupType && jobExternalStakeholdersPopup}
        <s.CapTabActionRow>
          <Button
            orange 
            solid
            className="ignore-in-pdf"
            onClick={() => setShowCapExportPopup(true)}
            sm
            type="button">{t('EXPORT_CAP')}
          </Button>
        </s.CapTabActionRow>
        <s.Left>
          <s.CapTitle>{t('CAP')}</s.CapTitle>
          <s.Block flex>
            <s.Half>
              <Field
                label={t('COLUMN_REPORT_NUMBER')}
                text={job.reportNumber}
              />
              <Field
                label={t('FACTORY')}
                text={factory ? factory.company.currentVersion.name : '-'}
              />
              <Field
                label={t('FACTORY_ADDRESS')}
                text={factoryAddresses && factoryAddresses.length ? parse(factoryAddresses) : '-'}
              />
              <Field
                label={t('COUNTRY')}
                text={country ? country.currentVersion.name : '-'}
              />
            </s.Half>
            <s.Half>
              <Field
                label={t('JOB_AUDIT_START_DATE')}
                text={
                  job.auditStartAt
                    ? toSimpleUtcDateString(job.auditStartAt)
                    : '-'
                }
              />
              <Field
                label={t('JOB_AUDIT_END_DATE')}
                text={
                  job.auditEndAt
                    ? toSimpleUtcDateString(job.auditEndAt)
                    : '-'
                }
              />
            </s.Half>
          </s.Block>
          <s.ExternalCAPUsersWrapper>
            <Styles.BlockHeader>{t('EXTERNAL_CAP_USERS')}</Styles.BlockHeader>
            {isExternalStakeholdersEditable &&
              <Styles.AddButtonWrapper
                onClick={() => setJobExternalStakeholdersPopupType(JobExternalStakeholdersPopupType.AddExisting)}
              >
                <Styles.AddButton name="plus" />
                <Styles.AddButtonLabel>{t('USER')}</Styles.AddButtonLabel>
              </Styles.AddButtonWrapper>
            }
          </s.ExternalCAPUsersWrapper>
          <s.Table>
            <thead>
            <tr>
              <td>{t('NAME')}</td>
              <td>{t('EMAIL')}</td>
              <td>{t('AFFILIATION')}</td>
              <td>{t('ACTION')}</td>
            </tr>
            </thead>
            <tbody>
            {jobExternalStakeholders.map(stakeholder => (
              <tr key={`stakeholder-${stakeholder.id}`}>
                <td>{`${stakeholder.user.firstName} ${stakeholder.user.lastName}`}</td>
                <td>{stakeholder.user.email}</td>
                <td>{t(JobExternalStakeholderRoleTranslation[stakeholder.role])}</td>
                <td>

                  {isExternalStakeholdersEditable &&
                    <Styles.ActionButton
                      disabled={isDeletingJobExternalStakeholder || isLoadingJobExternalStakeholders}
                      isDeleteButton
                      onClick={async () => {
                        const response = await deleteJobExternalStakeholder({
                          id: stakeholder.id,
                        });

                        if (response) {
                          refetchJobExternalStakeholders();
                        }
                      }}
                      sm
                      solid
                      type="button"
                    >
                      {t('DELETE')}
                    </Styles.ActionButton>
                  }
                </td>
              </tr>
            ))}
            </tbody>
          </s.Table>
          <s.CapOverallStatus>
            <CapOverallStatus
              editable={isExternalStakeholdersEditable}
              jobId={jobId}
              setOverallCapStatusValue={setOverallCapStatusValue}
              value={overallCapStatusValue}
            />
          </s.CapOverallStatus>
          <s.CapSortingDiv>
            {t('SHOW')}:
            {tags.map(tag => (
              <s.ButtonSpan
                key={tag}
                onClick={() => setFilterTag(tag)}
                selected={filterTag === tag}
              >
                {`${tagsLabels[tag]} ${filteredFindingsByStatus(tag).length}`}
              </s.ButtonSpan>
            ))}
          </s.CapSortingDiv>
          {!loading &&
          findingsGroupByChecklist.map(findingsByChecklist => {
            return(
              // eslint-disable-next-line
              <s.CapSortingDiv>
                <Styles.BlockHeader>{findingsByChecklist?.checklist?.currentVersion?.name}</Styles.BlockHeader>
                  {findingsByChecklist.findings.map(finding => {
                    return (
                      <s.CapBoxDiv key={finding.id}>
                        <CapFindingContainer
                          key={finding.id}
                          checklist={findingsByChecklist?.checklist}
                          allExternalUsersId={allExternalUsersId}
                          canDonwloadAttachment={canDownloadAttachment}
                          finding={finding}
                          isActionEditible={isActionEditable(finding.status)}
                          isFindingEditible={isFindingEditable(finding.status)}
                          isResponsiblePersonEditable={isResponsiblePersonEditable(finding.status)}
                          jobStatus={jobStatus}
                          refetchCapHistories={refetchCapHistories}
                          updateCapQueryCache={updateCapQueryCache}
                        />
                      </s.CapBoxDiv>
                    );
                  })}
              </s.CapSortingDiv>
            )})}
        </s.Left>
        <s.Right className="ignore-in-pdf">
          <s.CollapsedBtn
            lang={i18n.language}
            onClick={() => {
              setInfoCollapsed(false);
            }}
          >
            {t('CAP_FEEDBACK_HISTORY')}
          </s.CollapsedBtn>
        </s.Right>
        <s.InfoOverlay hidden={infoCollapsed}>
          <s.FeedbackHistoryText>
            {t('CAP_FEEDBACK_HISTORY')}
          </s.FeedbackHistoryText>
          <s.CloseBtn name="cross" onClick={() => setInfoCollapsed(true)} />
          <s.HistoryDiv>
            <s.HistoryRow key={0}>
              <s.ActivityColumn title>{t('CAP_ACTIVITY')}</s.ActivityColumn>
              <s.IdColumn title>{t('CAP_ID')}</s.IdColumn>
              <s.SubmittedOnColumn title>
                {t('CAP_SUBMITTED_ON')}
              </s.SubmittedOnColumn>
              <s.SubmittedByColumn title>
                {t('CAP_SUBMITTED_BY')}
              </s.SubmittedByColumn>
            </s.HistoryRow>
            <s.HistoryLine />
            {!capHistoriesLoading &&
              capHistoriesData &&
              capHistoriesData.capHistories.map(history => {
                return (
                  <s.HistoryRow key={history.id}>
                    <s.ActivityColumn>{history.activity}</s.ActivityColumn>
                    <s.IdColumn>{history.checklistQuestionId}</s.IdColumn>
                    <s.SubmittedOnColumn>
                      {toSimpleUtcDateString(history.submittedAt)}
                    </s.SubmittedOnColumn>
                    <s.SubmittedByColumn>{`${history?.submittedByUser?.lastName || ''} ${history?.submittedByUser?.firstName || ''}`}</s.SubmittedByColumn>
                  </s.HistoryRow>
                );
              })}
          </s.HistoryDiv>
        </s.InfoOverlay>
      </s.Wrapper>
    </>
  );
};

CapBox.propTypes = CapBoxPropTypes;
CapBox.defaultProps = CapBoxDefaultProps;

export default React.memo(CapBox);
